// src/pages/RefundPolicy.jsx

import { Link } from "react-router-dom";
import "./RefundPolicy.css";

function RefundPolicy() {
  return (
    <main className="refund-page">
      <div className="refund-container">
        {/* ================= HEADER ================= */}

        <header className="refund-header">
          <span className="refund-eyebrow">LEAP LEARNING</span>

          <h1>Refund &amp; Cancellation Policy</h1>

          <p>
            Policy governing cancellation, withdrawal, postponement and refund
            of applicable fees.
          </p>
        </header>

        {/* ================= CONTENT ================= */}

        <div className="refund-content">
          <section className="refund-section">
            <h2>1. Purpose</h2>

            <p>
              This Refund &amp; Cancellation Policy (“Policy”) governs the
              cancellation, withdrawal, postponement and refund of fees paid by
              any <strong>Student, Candidate or Participant</strong>
              (“Candidate”, “Student”, “Participant”, “you” or “your”) for
              programs, admission assistance, academic consultancy or other
              services provided or facilitated by
              <strong> Leap Learning</strong> (“Leap Learning”, “Company”, “we”,
              “us” or “our”).
            </p>

            <p>
              By making any payment, submitting an application, confirming
              admission or proceeding with enrollment, the Candidate
              acknowledges that they have read, understood and agreed to this
              Policy.
            </p>
          </section>

          <section className="refund-section">
            <h2>2. Registration Fee</h2>

            <p>
              The{" "}
              <strong>
                registration fee quoted and communicated at the time of
                admission or registration shall be non-refundable
              </strong>
              .
            </p>

            <p>
              The registration fee covers administrative and
              registration-related activities associated with processing the
              Candidate's application and admission formalities.
            </p>

            <p>
              Accordingly, where a refund is otherwise applicable under this
              Policy, the applicable registration fee shall be deducted from the
              refundable amount.
            </p>
          </section>

          <section className="refund-section">
            <h2>3. Cancellation or Postponement</h2>

            <p>
              A Candidate who wishes to{" "}
              <strong>
                cancel or postpone their admission within 7 calendar days from
                the date of admission confirmation
              </strong>{" "}
              may submit a refund request.
            </p>

            <p>
              Where the cancellation or postponement request is received within
              this period:
            </p>

            <ul>
              <li>
                the eligible amount paid towards the applicable program or
                service shall be refundable;
              </li>

              <li>
                the applicable registration fee shall be retained and shall not
                be refunded; and
              </li>

              <li>
                any amount already paid or committed to a university,
                institution, academic partner or other third party shall be
                subject to the applicable terms and refund policy of that
                respective entity.
              </li>
            </ul>

            <p>
              The refund request must be submitted through an official
              communication channel of Leap Learning.
            </p>
            <p>
              <strong>
                No refund shall ordinarily be available for cancellation,
                withdrawal or postponement requests submitted after 7 calendar
                days from the date of admission confirmation.
              </strong>
            </p>

            <p>
              After expiry of the 7-day refund period, amounts paid towards the
              program, admission assistance, consultancy or related services
              shall generally be treated as non-refundable, subject to
              applicable law and any specific written commitment made by Leap
              Learning.
            </p>
          </section>

          <section className="refund-section">
            <h2>4. Cancellation or Withdrawal During an Ongoing Program</h2>

            <p>
              If a Candidate voluntarily chooses to{" "}
              <strong>
                discontinue, withdraw from, cancel or postpone an ongoing
                program or any stage of the admission or academic process after
                the applicable 7-day refund period
              </strong>
              , no refund shall ordinarily be granted.
            </p>

            <p>This includes situations where the Candidate:</p>

            <ul>
              <li>changes their decision to pursue the program;</li>
              <li>changes their academic or professional plans;</li>
              <li>is unable to continue due to personal circumstances;</li>
              <li>becomes unavailable to participate;</li>
              <li>
                fails to complete required formalities within the prescribed
                timeline; or
              </li>
              <li>
                voluntarily chooses to discontinue the program for any other
                reason.
              </li>
            </ul>

            <p>
              This provision remains subject to applicable law and any statutory
              rights that cannot legally be excluded.
            </p>
          </section>

          <section className="refund-section">
            <h2>5. Postponement or Deferment</h2>

            <p>
              A request to postpone, defer or shift a program shall be treated
              as a cancellation/postponement request for the purposes of this
              Policy.
            </p>

            <p>
              A postponement request submitted{" "}
              <strong>within 7 calendar days of admission confirmation</strong>{" "}
              may be considered in accordance with Clause 3, subject to program
              availability and applicable program terms.
            </p>

            <p>
              A postponement request submitted{" "}
              <strong>
                after the 7-day period or after commencement of the applicable
                program or service
              </strong>{" "}
              shall ordinarily not qualify for a refund.
            </p>

            <p>
              Where the concerned university or institution has a separate
              deferment or postponement policy, the Candidate shall also be
              required to comply with that policy.
            </p>
          </section>

          <section className="refund-section">
            <h2>6. Delay in Completion of Formalities and Fee Payments</h2>

            <p>
              Where a Candidate fails to complete any required admission,
              academic, documentation or fee-related formalities within the
              prescribed timeline, and such delay continues for more than 15
              calendar days without prior written approval from Leap Learning,
              Leap Learning reserves the right to cancel or suspend the
              Candidate's admission or enrollment with immediate effect.
            </p>

            <p>
              If the Candidate wishes to resume or reinstate the admission or
              program following such cancellation or suspension, reinstatement
              shall be subject to the availability and applicable requirements
              of the program, approval by Leap Learning and/or the concerned
              university or institution, and payment of a reinstatement charge
              of USD 500 (Five Hundred United States Dollars), subject to
              applicable law and the applicable program terms.
            </p>

            <p>
              Payment of the reinstatement charge shall not, by itself,
              guarantee reinstatement or continuation of the program. Any
              pending fees, academic requirements, documentation or other
              applicable formalities must also be completed before reinstatement
              can be considered.
            </p>
          </section>

          <section className="refund-section">
            <h2>7. University, Institution and Third-Party Fees</h2>

            <p>
              Where any portion of the amount paid by the Candidate has been
              transferred, paid or committed to a university, institution,
              academic partner, examination body, payment service provider or
              other third party, the refund of such amount shall be governed by
              the applicable policy of that respective entity.
            </p>

            <p>
              Leap Learning does not guarantee a refund of an amount that is
              exclusively controlled or retained by a third party.
            </p>

            <p>
              Where applicable, Leap Learning may assist the Candidate in
              communicating with the relevant institution or service provider
              regarding the refund process.
            </p>
          </section>

          <section className="refund-section">
            <h2>8. Cancellation of a Program or Service by Leap Learning</h2>

            <p>
              If Leap Learning is unable to provide a program or service that
              has been paid for due to circumstances within its responsibility,
              Leap Learning shall communicate the applicable options to the
              affected Candidate.
            </p>

            <p>
              Where a refund is applicable, it shall be processed in accordance
              with the applicable agreement, program terms, payment arrangements
              and applicable law.
            </p>

            <p>
              Where the program or service is controlled by a university,
              institution or other third party, the applicable third-party
              policy may govern the relevant refund.
            </p>
          </section>

          <section className="refund-section">
            <h2>9. Duplicate or Excess Payments</h2>

            <p>
              If a Candidate makes a duplicate payment or pays an amount in
              excess of the applicable fee, the Candidate should notify Leap
              Learning as soon as reasonably possible.
            </p>

            <p>
              After verification of the transaction, the eligible excess amount
              may be refunded through the applicable payment channel.
            </p>
          </section>

          <section className="refund-section">
            <h2>10. Refund Processing</h2>

            <p>
              Once a refund is approved and the required verification has been
              completed, the eligible refund shall ordinarily be processed
              within <strong>7–15 business days</strong>.
            </p>

            <p>
              The actual time taken for the amount to be credited may vary
              depending on the bank, payment gateway or other payment service
              provider involved.
            </p>
          </section>

          <section className="refund-section">
            <h2>11. Mode of Refund</h2>

            <p>
              Approved refunds shall ordinarily be made through the
              <strong> original payment method used by the Candidate</strong>.
            </p>

            <p>
              Where necessary, Leap Learning may request appropriate
              verification before processing a refund.
            </p>

            <p>
              Refunds shall not ordinarily be transferred to an unrelated
              third-party account.
            </p>
          </section>

          <section className="refund-section">
            <h2>12. Non-Refundable Charges</h2>

            <p>
              Apart from the registration fee, any charge specifically
              identified as <strong>non-refundable</strong> in the applicable
              fee structure, invoice, admission communication, program terms or
              written agreement shall be treated as non-refundable, subject to
              applicable law.
            </p>
          </section>

          <section className="refund-section">
            <h2>13. Non-Attendance or Failure to Participate</h2>

            <p>
              Failure to attend, participate in or utilize a program, academic
              session, counselling session, meeting or other service after
              enrollment shall not by itself create an entitlement to a refund
              once the applicable refund period has expired.
            </p>
          </section>

          <section className="refund-section">
            <h2>14. Incomplete Admission Formalities</h2>

            <p>
              Where a Candidate fails to provide required documents,
              information, declarations or other formalities within the
              prescribed timeline, such failure shall not automatically create
              an entitlement to a refund after the applicable refund period has
              expired.
            </p>
          </section>

          <section className="refund-section">
            <h2>15. False or Misleading Information</h2>

            <p>
              If a Candidate provides false, fraudulent, forged or materially
              misleading information or documentation, Leap Learning may suspend
              or terminate the relevant services or admission assistance.
            </p>

            <p>
              Any refund in such circumstances shall be determined in accordance
              with the applicable program terms, institutional policies and
              applicable law.
            </p>
          </section>

          <section className="refund-section">
            <h2>16. Changes in Academic or Institutional Requirements</h2>

            <p>
              Universities, institutions and other academic authorities may
              change their academic requirements, schedules, procedures,
              eligibility criteria or other conditions.
            </p>

            <p>
              Where such changes are made by an external institution or
              authority, any applicable refund or cancellation rights shall be
              determined in accordance with the relevant institutional policy,
              applicable contractual terms and applicable law.
            </p>
          </section>

          <section className="refund-section">
            <h2>17. Exceptional Circumstances</h2>

            <p>
              Requests arising from exceptional or unforeseen circumstances may
              be reviewed by Leap Learning on a{" "}
              <strong>case-by-case basis</strong>.
            </p>

            <p>
              Such review shall not automatically create an entitlement to a
              refund. Any decision shall remain subject to applicable
              contractual terms, third-party policies and applicable law.
            </p>
          </section>

          <section className="refund-section">
            <h2>18. No Waiver of Statutory Rights</h2>

            <p>
              Nothing in this Policy is intended to exclude, restrict or waive
              any statutory right, remedy or protection available to a Candidate
              or consumer under applicable law.
            </p>

            <p>
              If any provision of this Policy conflicts with a mandatory legal
              requirement, the applicable legal requirement shall prevail to the
              extent of such conflict.
            </p>

            <p>
              The applicable consumer-protection framework recognizes consumer
              rights and regulates unfair contractual terms and service-related
              obligations.
            </p>
          </section>

          <section className="refund-section">
            <h2>19. Policy Amendments</h2>

            <p>
              Leap Learning may update or amend this Refund &amp; Cancellation
              Policy from time to time to reflect changes in its programs,
              services, institutional requirements, business practices or
              applicable laws.
            </p>

            <p>
              The revised Policy shall be published on the Leap Learning website
              with the applicable <strong>Effective Date</strong>
              or <strong>Last Updated</strong> date.
            </p>
          </section>

          <section className="refund-section">
            <h2>20. Governing Law</h2>

            <p>
              This Policy shall be governed by and interpreted in accordance
              with <strong>applicable law and regulations</strong>.
            </p>

            <p>
              Any dispute concerning cancellation, withdrawal or refund shall be
              addressed in accordance with the applicable agreement, grievance
              mechanism and legal remedies available to the concerned party.
            </p>
          </section>
        </div>
      </div>
    </main>
  );
}

export default RefundPolicy;
